import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  defer,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppConstants, RELATIONSHIP_CODE } from 'src/app/app.constants';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { MultilingualService } from 'src/app/core/services/multilingual/multilingual.service';
import { ContactInfo } from 'src/app/interfaces/contact-info.interface';
import { FamilyCoverageResponse } from 'src/app/interfaces/family-coverage-response.interface';
import { Member } from 'src/app/interfaces/member.interface';
import { ShippingAddress } from 'src/app/interfaces/shipping-address.interface';
import {
  AppCenterAnalytics,
  AppCenterAnalyticsAPIMsgs,
} from 'src/app/services/app-insights/app-insights';
import { AppInsightsService } from 'src/app/services/app-insights/app-insights.service';
import { CmsService } from 'src/app/services/cms/cms.service';
import { MailOrderProfileEnrollmentService } from 'src/app/services/mail-order-profile-enrollment/mail-order-profile-enrollment.service';
import { UserService } from 'src/app/services/user/user.service';
import { IPmartProfileEnrollRequest } from '../../interfaces/request/pmart-profile-enroll-request.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { states } from '../../main/main.constants';
import { calculateAge } from '../util';
import { NotificationsBody } from 'src/app/interfaces/notifications-body.interface';

interface MemberInfo {
  cardId: string;
  clientId: string;
  personCode: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string;
  relationshipCode: string;
  cellPhone: string;
  email?: string;
}
@Component({
  selector: 'app-pmart-patient-enrollment-form',
  templateUrl: './mail-order-patient-enrollment-form.component.html',
  styleUrls: ['./mail-order-patient-enrollment-form.component.scss'],
})
export class PmartPatientEnrollmentFormComponent implements OnInit, OnDestroy {
  @ViewChild('success', { read: TemplateRef })
  successTemplate!: TemplateRef<any>;
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;

  private subscription: Subscription = new Subscription();
  public profileFormGroup!: FormGroup;
  public faxDoctorForPrescriptions!: FormGroup;
  public transferFromPharmacy!: FormGroup;
  public familyCoverageResponse!: FamilyCoverageResponse | null;
  public isSignatureMatch: boolean = true;
  public isOtherAllergySelected: boolean = false;
  public errorMsg!: string;
  public states = states;
  public externalClientIdsMapping = AppConstants.EXTERNAL_CLIENT_IDS_MAPPING;

  public memberInfo!: MemberInfo;

  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  shippingAddressesResponse$!: Observable<ShippingAddress[]>;
  shippingAddressesResponse: ShippingAddress[] = [];
  acknowledgmentLinks$!: Observable<{
    privacyPolicy: string;
    patientBillRights: string;
  }>;
  public tabIndex: number = 0;

  get otherMedicationsArray() {
    const control = <FormArray>(
      (<FormGroup>this.profileFormGroup.get('otherMedications')).get('otherM')
    );
    return control;
  }

  get otherAllergiesArray() {
    const control = <FormArray>(
      (<FormGroup>this.profileFormGroup.get('otherAllergies')).get('otherA')
    );
    return control;
  }

  get faxDetailsArray() {
    const control = <FormArray>(
      (<FormGroup>this.profileFormGroup.get('faxDetails')).get('details')
    );
    return control;
  }

  get medicalConditionArray() {
    const control = <FormArray>(
      (<FormGroup>this.profileFormGroup.get('listOfMedicalConditions')).get(
        'list'
      )
    );
    return control;
  }

  get prescriptionsArray() {
    const control = <FormArray>(
      (<FormGroup>this.profileFormGroup.get('prescriptions')).get(
        'prescriptionDetails'
      )
    );
    return control;
  }

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private enrollmentService: MailOrderProfileEnrollmentService,
    private authService: AuthService,
    private readonly dialogRef: MatDialogRef<PmartPatientEnrollmentFormComponent>,
    public multilingualService: MultilingualService,
    private cmsService: CmsService,
    private appInsightsService: AppInsightsService,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public dialogData?: { member: Member; pageName?: string }
  ) {}

  public allergiesList = [
    'Aspirin',
    'Cephalosporin',
    'Codeine',
    'Erythromycin',
    'Latex',
    'NSAIDs',
    'Peanuts',
    'Penicillin',
    'Sulfa',
    'Other',
  ];

  ngOnInit(): void {
    this.appInsightsService.trackPageView(
      `${AppCenterAnalytics.PATIENT_ENROLLMENT_FORM_VIEWED}`,
      { member: this.dialogData }
    );

    this.familyCoverageResponse = this.userService.allFamilyCoverageResponse;
    this.populateMemberDetails();
    this.acknowledgmentLinks$ = this.getAcknowledgmentLinks();
    this.profileFormGroup = this.createFormGroup();

    if (
      this.profileFormGroup.controls.prescriptionsTransfer.value ===
      'faxDoctorForMedications'
    ) {
      this.profileFormGroup.addControl(
        'faxDetails',
        this.formBuilder.group({
          details: this.formBuilder.array([this.addOneMoreFaxDetails()]),
        })
      );
    }

    this.subscription.add(
      this.profileFormGroup.controls.allergies.valueChanges.subscribe((v) => {
        const index = this.allergiesList.indexOf('Other');
        if (v[index]) {
          this.isOtherAllergySelected = true;
        } else {
          this.isOtherAllergySelected = false;
        }
      })
    );

    this.subscription.add(
      this.profileFormGroup.controls.primaryLanguage.valueChanges.subscribe(
        (res) => {
          if (res === 'other') {
            this.profileFormGroup.addControl(
              'otherLanguage',
              this.formBuilder.control('', Validators.required)
            );
          } else {
            this.profileFormGroup.removeControl('otherLanguage');
          }
        }
      )
    );

    this.subscription.add(
      this.profileFormGroup.controls.controlledSubstances.valueChanges.subscribe(
        (value) => {
          if (value === 'true') {
            this.profileFormGroup.addControl(
              'driverLicenseNumber',
              this.formBuilder.control('', Validators.required)
            );
          } else {
            this.profileFormGroup.removeControl('driverLicenseNumber');
          }
        }
      )
    );

    this.subscription.add(
      this.profileFormGroup.controls.signature.valueChanges.subscribe((v) => {
        const name = `${this.memberInfo.firstName}${this.memberInfo.lastName}`;
        if (
          v.replace(/\s/g, '').toLowerCase() !==
          name.replace(/\s/g, '').toLowerCase()
        ) {
          this.isSignatureMatch = false;
        } else {
          this.isSignatureMatch = true;
        }
      })
    );

    this.subscription.add(
      this.profileFormGroup.controls.prescriptionsTransfer.valueChanges.subscribe(
        (value) => {
          this.profileFormGroup.removeControl('prescriptions');
          this.profileFormGroup.removeControl('currentPharmacy');
          this.profileFormGroup.removeControl('pharmacyPhoneNumber');
          this.profileFormGroup.removeControl('faxDetails');
          this.profileFormGroup.removeControl('otherPrescriptionInstructions');
          if (value === 'faxDoctorForMedications') {
            this.profileFormGroup.addControl(
              'faxDetails',
              this.formBuilder.group({
                details: this.formBuilder.array([this.addOneMoreFaxDetails()]),
              })
            );
          }
          if (value === 'other') {
            this.profileFormGroup.addControl(
              'otherPrescriptionInstructions',
              this.formBuilder.control('', Validators.required)
            );
          }
          if (value === 'transferFromPharmacy') {
            this.profileFormGroup.addControl(
              'prescriptions',
              this.formBuilder.group({
                prescriptionDetails: this.formBuilder.array(
                  [this.addPrescriptionControl()],
                  Validators.required
                ),
              })
            );
            this.profileFormGroup.addControl(
              'currentPharmacy',
              this.formBuilder.control('', Validators.required)
            );
            this.profileFormGroup.addControl(
              'pharmacyPhoneNumber',
              this.formBuilder.control('', [
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(10),
                Validators.pattern('^[0-9]+$'),
              ])
            );
          }
        }
      )
    );

    this.allergiesList.forEach(() => {
      (this.profileFormGroup.get('allergies') as FormArray).push(
        this.formBuilder.control(false)
      );
    });
  }

  private createFormGroup() {
    return this.formBuilder.group({
      homePhoneNumber: [
        '',
        [
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern('^[0-9]+$'),
        ],
      ],
      addressLine1: ['', [Validators.required, Validators.maxLength(55)]],
      addressLine2: ['', [Validators.maxLength(55)]],
      zipCode: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(5),
          Validators.pattern('^[0-9]+$'),
        ],
      ],
      city: [
        '',
        [
          Validators.required,
          Validators.maxLength(35),
          // Validators.pattern(/^[a-zA-Z]+$/),
        ],
      ],
      state: ['', Validators.required],
      primaryLanguage: ['English', Validators.required],
      haveAllergies: [false, Validators.required],
      controlledSubstances: ['false', Validators.required],
      additionalComments: [''],
      acknowledgePharmacyPrivacyPolicies: [false, Validators.requiredTrue],
      haveViewedPatientBillOfRights: [false, Validators.requiredTrue],
      signature: ['', Validators.required],
      textNotification: [false],
      emailNotification: [false],
      hasDrugAllergies: ['0', Validators.required],
      allergies: this.formBuilder.array([]),
      selectedAddressIndex: [0, Validators.required],
      hasMedicalConditions: ['0', Validators.required],
      otherMedications: this.formBuilder.group({
        otherM: this.formBuilder.array([this.addOtherMedicalCondition()]),
      }),
      listOfMedicalConditions: this.formBuilder.group({
        list: this.formBuilder.array([this.addMedicalCondition()]),
      }),
      otherAllergies: this.formBuilder.group({
        otherA: this.formBuilder.array([this.addOtherAllergiesControl()]),
      }),
      prescriptionsTransfer: ['doctorSendMedications', Validators.required],
    });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.tabIndex = event.index;
    if (event.index === 0) {
      this.profileFormGroup.addControl(
        'addressLine1',
        this.formBuilder.control('', [
          Validators.required,
          Validators.maxLength(35),
        ])
      );
      this.profileFormGroup.addControl(
        'addressLine2',
        this.formBuilder.control('', Validators.maxLength(55))
      );
      this.profileFormGroup.addControl(
        'city',
        this.formBuilder.control('', [
          Validators.required,
          Validators.maxLength(35),
          // Validators.pattern(/^[a-zA-Z]+$/),
        ])
      );
      this.profileFormGroup.addControl(
        'state',
        this.formBuilder.control('', Validators.required)
      );
      this.profileFormGroup.addControl(
        'zipCode',
        this.formBuilder.control('', [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(5),
          Validators.pattern('^[0-9]+$'),
        ])
      );
    } else {
      this.profileFormGroup.removeControl('addressLine1');
      this.profileFormGroup.removeControl('addressLine2');
      this.profileFormGroup.removeControl('city');
      this.profileFormGroup.removeControl('state');
      this.profileFormGroup.removeControl('zipCode');
    }
  }

  public addOneMoreFaxDetails() {
    return this.formBuilder.group({
      prescriberName: ['', Validators.required],
      prescriberPhoneNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern('^[0-9]+$'),
        ],
      ],
      prescriberFaxNumber: ['', Validators.required],
      prescriptionName: ['', Validators.required],
      prescriptionStrength: ['', Validators.required],
    });
  }

  public addOtherAllergiesControl() {
    return this.formBuilder.group({
      allergy: [''],
    });
  }

  public addPrescriptionControl() {
    return this.formBuilder.group({
      prescriptionDetail: ['', Validators.required],
    });
  }

  public addOtherMedicalCondition() {
    return this.formBuilder.group({
      otherCondition: [''],
    });
  }

  public addMedicalCondition() {
    return this.formBuilder.group({
      condition: [''],
    });
  }

  public numericOnly(event: KeyboardEvent): void {
    const allowedKeys: Array<string> = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    const pattern = /[0-9]/;

    if (allowedKeys.includes(event.key) || pattern.test(event.key)) {
      return;
    }

    event.preventDefault();
  }

  public addOneMoreMedicalCondition() {
    this.medicalConditionArray.push(this.addMedicalCondition());
  }

  public addOneMoreAllergy() {
    this.otherAllergiesArray.push(this.addOtherAllergiesControl());
  }

  public addOneMorePrescription() {
    this.prescriptionsArray.push(this.addPrescriptionControl());
  }

  public removePrescription(index: number) {
    this.prescriptionsArray.removeAt(index);
  }

  public removeMedicalCondition(index: number) {
    this.medicalConditionArray.removeAt(index);
  }

  public removeFaxDetails(index: number) {
    this.faxDetailsArray.removeAt(index);
  }

  public addMoreOtherMedication() {
    this.otherMedicationsArray.push(this.addOtherMedicalCondition());
  }

  public addMoreOtherFlexDetails() {
    this.faxDetailsArray.push(this.addOneMoreFaxDetails());
  }

  public removeOtherMedication(index: number) {
    this.otherMedicationsArray.removeAt(index);
  }

  public removeOtherAllergy(index: number) {
    this.otherAllergiesArray.removeAt(index);
  }

  private populateMemberDetails() {
    const clientId = this.userService.eligibilityVerificationResponse?.clientId;
    const cardId = this.userService.eligibilityVerificationResponse?.cardId;
    const personCode = this.dialogData?.member.personCode;

    if (clientId && cardId && personCode) {
      const memberDetails = this.familyCoverageResponse?.members.find(
        (x) => x.personCode == personCode
      );

      // fetch card holder details of the family
      const cardHolderDetails = this.userService.isOrionUser()
        ? this.familyCoverageResponse?.members.find(
            (x) => x.relationshipCode === RELATIONSHIP_CODE.CARDHOLDER
          )
        : this.familyCoverageResponse?.members.find(
            (x) => x.relationshipCode === undefined
          );

      if (memberDetails) {
        this.memberInfo = this.getMemberDetails(
          cardId,
          clientId,
          memberDetails
        );

        this.isLoading$.next(true);
        this.subscription.add(
          defer(() => {
            this.getShippingAddresses(
              clientId,
              cardId,
              memberDetails.personCode
            );

            if (this.isMinorDependent(this.memberInfo)) {
              // for members with age < 18, use cardholders contact details
              if (cardHolderDetails) {
                return this.getContactInfo(
                  clientId,
                  cardId,
                  cardHolderDetails.personCode
                );
              } else {
                return of({ mobileNumber: '', email: '' });
              }
            } else {
              this.getShippingNotificationSettings(
                clientId,
                cardId,
                memberDetails.personCode
              );
              return this.getContactInfo(
                clientId,
                cardId,
                memberDetails.personCode
              );
            }
          })
            .pipe(take(1))
            .subscribe(
              (res) => {
                this.memberInfo.cellPhone = res.mobileNumber.toString();
                this.memberInfo.email = res.email;
                // console.log('member info', this.memberInfo);
                this.isLoading$.next(false);
              },
              (err) => {
                this.isLoading$.next(false);
              }
            )
        );
      }
    }
  }

  private getMemberDetails(
    cardId: string,
    clientId: string,
    memberDetails: Member
  ) {
    return {
      cardId: cardId,
      clientId: clientId,
      firstName: memberDetails.firstName ? memberDetails.firstName : '',
      lastName: memberDetails.lastName ? memberDetails.lastName : '',
      personCode: memberDetails.personCode ? memberDetails.personCode : '',
      dateOfBirth: memberDetails.dateOfBirth ? memberDetails.dateOfBirth : null,
      gender: memberDetails.gender ? memberDetails.gender : '',
      cellPhone: '',
      relationshipCode: memberDetails.relationshipCode
        ? memberDetails.relationshipCode
        : '',
    };
  }

  /**
   * @param memberInfo Member details for which the form is getting submitted
   * @returns If the member is a minor dependent with age < 18
   */
  public isMinorDependent(memberInfo: MemberInfo): boolean {
    const loggedInMember = this.userService.eligibilityVerificationResponse;
    if (memberInfo.personCode == loggedInMember?.personCode) {
      return false; // if member is logged-in, return false
    } else if (memberInfo.dateOfBirth) {
      const age = calculateAge(memberInfo.dateOfBirth);
      return age < 18;
    }
    return false;
  }

  private getContactInfo(
    clientId: string,
    cardId: string,
    personCode: string
  ): Observable<{ email: string; mobileNumber: string }> {
    this.isLoading$.next(true);
    return defer(() => {
      if (this.authService.isAuthorizedByAzureB2c) {
        return this.enrollmentService
          .getContactInfo(clientId, cardId, personCode)
          .pipe(
            map((data: ContactInfo) => {
              return {
                mobileNumber: data.mobileNumber || data.contactNumber,
                email: data.email,
              };
            })
          );
      } else {
        return this.enrollmentService
          .getContactSSOInfo(clientId, cardId, personCode)
          .pipe(
            map((data: ContactInfo) => {
              return {
                mobileNumber: data.mobileNumber || data.contactNumber,
                email: data.email,
              };
            })
          );
      }
    }).pipe(tap(() => this.isLoading$.next(false)));
  }

  public getShippingAddresses(
    clientId: string,
    cardId: string,
    personCode: string
  ): void {
    this.shippingAddressesResponse$ = this.enrollmentService
      .getShippingAddresses(clientId, cardId, personCode)
      .pipe(
        take(1),
        map((res) => {
          this.shippingAddressesResponse = res.shippingAddresses || [];
          return this.shippingAddressesResponse;
        }),
        catchError(() => {
          this.shippingAddressesResponse = [];
          return of(this.shippingAddressesResponse);
        })
      );
  }

  public getAcknowledgmentLinks(): Observable<{
    privacyPolicy: string;
    patientBillRights: string;
  }> {
    return this.cmsService.getLinks().pipe(
      map((links) => {
        const privacyPolicy = links.find(
          (x) =>
            x.pageName.toLowerCase() === 'mail order enrollment- privacy policy'
        )?.linkUrl;
        const patientBillRights = links.find(
          (x) =>
            x.pageName.toLowerCase() ===
            'mail order enrollment- patient bill of rights'
        )?.linkUrl;
        return {
          privacyPolicy: privacyPolicy ? privacyPolicy : '',
          patientBillRights: patientBillRights ? patientBillRights : '',
        };
      })
    );
  }

  public privacyPolicyClicked(): void {
    this.appInsightsService.trackEvent(
      AppCenterAnalytics.PATIENT_ENROLLMENT_PRIVACY_POLICY_CLICKED
    );
  }

  public patientBillOfRightsClicked(): void {
    this.appInsightsService.trackEvent(
      AppCenterAnalytics.PATIENT_ENROLLMENT_BILL_RIGHTS_CLICKED
    );
  }

  public createMailOrderProfile() {
    this.appInsightsService.trackEvent(
      `${AppCenterAnalytics.PATIENT_ENROLLMENT_FORM_SUBMIT_CLICKED}`
    );

    const req: IPmartProfileEnrollRequest = {
      cardId: this.memberInfo.cardId,
      personCode: this.memberInfo.personCode,
      clientID: this.memberInfo.clientId,
      firstName: this.memberInfo.firstName,
      lastName: this.memberInfo.lastName,
      dateOfBirth: `${
        this.memberInfo.dateOfBirth?.toString() as string
      }T00:00:00Z`,
      gender: this.memberInfo.gender as string,
      cellPhone: this.memberInfo.cellPhone,
      homePhone: this.profileFormGroup.value.homePhoneNumber,
      email: this.memberInfo.email as string,
      primaryLanguage: this.profileFormGroup.value.primaryLanguage,
      groupId: this.memberInfo.clientId,
      newPrescriptionTransfers:
        this.profileFormGroup.value.prescriptionsTransfer,
      additionalComments: this.profileFormGroup.value.additionalComments,
      acknowledgePharmacyPrivacyPolicies:
        this.profileFormGroup.value.acknowledgePharmacyPrivacyPolicies,
      haveViewedPatientBillOfRights:
        this.profileFormGroup.value.haveViewedPatientBillOfRights,
      signature: this.profileFormGroup.value.signature,
      controlledSubstances:
        this.profileFormGroup.value.controlledSubstances === 'true',
    };
    const otherMedicalConditions: string[] = [];
    this.profileFormGroup.value.otherMedications.otherM.forEach(
      (p: { otherCondition: string }) => {
        if (p.otherCondition) {
          otherMedicalConditions.push(p.otherCondition);
        }
      }
    );
    req.otherMedications = otherMedicalConditions;
    if (
      this.profileFormGroup.value.prescriptionsTransfer ===
      'faxDoctorForMedications'
    ) {
      req.faxDoctorForMedications =
        this.profileFormGroup.value.faxDetails.details;
    } else if (
      this.profileFormGroup.value.prescriptionsTransfer ===
      'transferFromPharmacy'
    ) {
      const prescriptions: string[] = [];
      this.profileFormGroup.value.prescriptions.prescriptionDetails.forEach(
        (p: { prescriptionDetail: string }) => {
          prescriptions.push(p.prescriptionDetail);
        }
      );
      req.transferFromPharmacy = {
        currentPharmacy: this.profileFormGroup.value.currentPharmacy,
        pharmacyPhoneNumber: this.profileFormGroup.value.pharmacyPhoneNumber,
        prescriptions: prescriptions,
      };
    } else if (this.profileFormGroup.value.prescriptionsTransfer === 'other') {
      req.otherPrescriptionInstructions =
        this.profileFormGroup.value.otherPrescriptionInstructions;
    }

    if (this.profileFormGroup.value.controlledSubstances === 'true') {
      req.driverLicenseNumber = this.profileFormGroup.value.driverLicenseNumber;
    }

    if (this.profileFormGroup.value.hasDrugAllergies === '1') {
      const otherAddedAllergies: string[] = [];
      const trueIndexes = this.findTrueIndexes(
        this.profileFormGroup.value.allergies
      );
      req.allergies = trueIndexes.length
        ? trueIndexes.map((index) => this.allergiesList[index])
        : [];
      if (this.isOtherAllergySelected) {
        this.profileFormGroup.value.otherAllergies.otherA.forEach(
          (a: { allergy: string }) => {
            if (a.allergy) {
              otherAddedAllergies.push(a.allergy);
            }
          }
        );
      }
      req.allergies = [...req.allergies, ...otherAddedAllergies];
      const index = req.allergies.indexOf('Other');
      if (index !== -1) {
        req.allergies.splice(index, 1);
      }
    }

    if (this.profileFormGroup.value.hasMedicalConditions === '1') {
      const medicalConditions: string[] = [];
      this.profileFormGroup.value.listOfMedicalConditions.list.forEach(
        (p: { condition: string }) => {
          if (p.condition) {
            medicalConditions.push(p.condition);
          }
        }
      );
      req.medicalConditions = medicalConditions;
    }

    if (req.primaryLanguage === 'other') {
      req.primaryLanguage = this.profileFormGroup.value.otherLanguage;
    }

    if (this.tabIndex === 0) {
      req.addressLine1 = this.profileFormGroup.value.addressLine1;
      req.addressLine2 = this.profileFormGroup.value.addressLine2;
      req.city = this.profileFormGroup.value.city;
      req.state = this.profileFormGroup.value.state;
      req.zip = this.profileFormGroup.value.zipCode;
    } else {
      const selectedIndex = this.profileFormGroup.value.selectedAddressIndex;
      if (this.shippingAddressesResponse.length > 0) {
        req.addressLine1 =
          this.shippingAddressesResponse[selectedIndex].addressLine1;
        req.addressLine2 =
          this.shippingAddressesResponse[selectedIndex].addressLine2;
        req.city = this.shippingAddressesResponse[selectedIndex].city;
        req.state = this.shippingAddressesResponse[selectedIndex].state;
        req.zip = this.shippingAddressesResponse[selectedIndex].zipCode;
      } else {
        // existing shipping addresses are not present
        return;
      }
    }

    this.isLoading$.next(true);
    this.enrollmentService
      .createProfile(req)
      .pipe(
        take(1),
        switchMap((res) => {
          return defer(() => {
            if (
              this.isMinorDependent(this.memberInfo) ||
              !this.userService.isOrionUser()
            ) {
              return of(res);
            } else {
              return this.updateShippingNotificationSettings(
                req.clientID,
                req.cardId,
                req.personCode,
                {
                  email: this.profileFormGroup.value.emailNotification,
                  sms: this.profileFormGroup.value.textNotification,
                }
              ).pipe(map(() => res));
            }
          });
        })
      )
      .pipe(take(1))
      .subscribe(
        (res) => {
          let responseText =
            AppCenterAnalyticsAPIMsgs.PATIENT_ENROLLMENT_PROFILE_UPDATED;
          this.appInsightsService.trackPageView(
            `${AppCenterAnalytics.PATIENT_ENROLLMENT_FORM_CREATE_API_SUCCESS}`,
            {
              formData: req,
              Response: responseText,
              pageName: this.dialogData?.pageName,
            }
          );
          this.snackBar.openFromTemplate(this.successTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
          this.close('submit');
          this.isLoading$.next(false);
        },
        (err) => {
          this.errorMsg = err;
          this.appInsightsService.trackPageView(
            `${AppCenterAnalytics.PATIENT_ENROLLMENT_FORM_CREATE_API_ERROR}`,
            {
              formData: req,
              Response: err,
              pageName: this.dialogData?.pageName,
            }
          );
          this.snackBar.openFromTemplate(this.errorTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
          this.isLoading$.next(false);
        }
      );
  }

  public close(flag: 'cancel' | 'submit'): void {
    this.dialogRef.close(flag);
  }

  public getAddressIndex(i: number) {
    return i + 1 < 10 ? `0${i + 1}` : i + 1;
  }

  private getShippingNotificationSettings(
    clientId: string,
    cardId: string,
    personCode: string
  ): void {
    let email: boolean = false;
    let sms: boolean = false;

    this.isLoading$.next(true);
    this.enrollmentService
      .getNotificationSettings(clientId, cardId, personCode)
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (res.mailOrderShippingNotification === 'BOTH') {
            email = true;
            sms = true;
          } else {
            email = res.mailOrderShippingNotification === 'EMAIL';
            sms = res.mailOrderShippingNotification === 'MOBILE';
          }
          this.profileFormGroup.controls.textNotification.setValue(sms);
          this.profileFormGroup.controls.emailNotification.setValue(email);
        },
        (err) => {
          this.isLoading$.next(false);
        }
      );
  }

  private updateShippingNotificationSettings(
    clientId: string,
    cardId: string,
    personCode: string,
    settings: { email: boolean; sms: boolean }
  ): Observable<NotificationsBody> {
    let result: string = 'NONE';
    if (settings.email && settings.sms) {
      result = 'BOTH';
    } else if (settings.email) {
      result = 'EMAIL';
    } else if (settings.sms) {
      result = 'MOBILE';
    }
    return this.enrollmentService.updateNotificationSettings(
      clientId,
      cardId,
      personCode,
      result
    );
  }

  private findTrueIndexes(arr: boolean[]): number[] {
    const trueIndexes = [];
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === true) {
        trueIndexes.push(i);
      }
    }
    return trueIndexes;
  }

  public signatureChange(): void {
    this.appInsightsService.trackPageView(
      `${AppCenterAnalytics.PATIENT_ENROLLMENT_FORM_SIGNATURE_CHANGED}`,
      { formData: { ...this.profileFormGroup.value, ...this.memberInfo } }
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
